import React from "react";
import { Star, ArrowRight } from "lucide-react";
import { reviews, googleMeta } from "../mock";
import Reveal from "./Reveal";
import CountUp from "./CountUp";

// Tasteful "G" badge (Google blue) — indicates Google Reviews without reproducing the trademarked multicolour logo.
const GBadge = ({ size = 40 }) => (
  <span
    className="inline-flex items-center justify-center rounded-full bg-white shadow-sm border border-neutral-200 font-display font-bold text-[#4285F4]"
    style={{ width: size, height: size, fontSize: size * 0.5 }}
    aria-label="Google"
  >
    G
  </span>
);

const Stars = ({ count = 5, size = 15 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} className={i < count ? "text-[#fbbc05] fill-[#fbbc05]" : "text-neutral-300"} />
    ))}
  </div>
);

const GoogleReviews = () => {
  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-5 lg:px-6">
        {/* Header / summary */}
        <Reveal className="bg-white rounded-[28px] border border-neutral-100 soft-shadow p-7 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-5">
            <GBadge size={56} />
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#1a1714] leading-tight">Google Reviews</h2>
              <p className="text-neutral-500 text-sm mt-1">What our clients say about iClean</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="font-display text-5xl font-bold text-[#1a1714] leading-none"><CountUp value={googleMeta.rating} /></div>
              <div className="mt-2 flex justify-center"><Stars count={5} size={18} /></div>
              <div className="text-xs text-neutral-500 mt-1.5">Based on <CountUp value={String(googleMeta.count)} /> reviews</div>
            </div>
            <a
              href={googleMeta.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] text-white font-semibold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              See all reviews <ArrowRight size={17} />
            </a>
          </div>
        </Reveal>

        {/* Review cards */}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <Reveal key={r.name} delay={(i % 3) * 80}>
              <div className="bg-white h-full rounded-[22px] p-6 border border-neutral-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold font-display flex-shrink-0"
                      style={{ background: r.color }}
                    >
                      {r.name.charAt(0)}
                    </span>
                    <div>
                      <div className="font-semibold text-[#1a1714] text-sm leading-tight">{r.name}</div>
                      <div className="text-xs text-neutral-400">{r.when}</div>
                    </div>
                  </div>
                  <GBadge size={26} />
                </div>
                <div className="mt-4"><Stars count={r.rating} /></div>
                <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
